import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Typography, Space, Modal, Form, Input, Select, message, Tooltip, Card, Row, Col, Statistic, Divider } from 'antd';
import { CheckCircleOutlined, EyeOutlined, CheckOutlined, ClockCircleOutlined, FileExcelOutlined, ArrowLeftOutlined, FormOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import AuditDetailModal from '../components/inventoryAudit/AuditDetailModal';
import { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

// Import clean code types and constants (maintain original UI)
import { INVENTORY_AUDIT_RESULT } from '../constants/inventoryAudit';
import AuditErrorBoundary from '../components/common/AuditErrorBoundary';

const { Title } = Typography;

interface AuditDetail {
  id: number;
  audit_id: number;
  asset_id: number;
  result: string;
  status: string;
  technical_condition?: string;
  asset_status?: string;
  audit_date?: string;
  location_id?: number;
  notes?: string;
  proposal?: string;
  type: string;
  snapshot?: string;
  accessories_snapshot?: string;
  created_at: string;
  updated_at: string;
}

interface InventoryAudit {
  id: number;
  name: string;
  code: string;
  company_id: number;
  start_date: string;
  end_date: string;
  status: string;
  created_by_user_id: number;
  description?: string;
  created_at: string;
  updated_at: string;
  type?: string; // Thêm dòng này để tránh lỗi linter
  assigned_to_employee_id?: number;
  location_id?: number;
  assigned_employee_name?: string;
  location_name?: string;
}

const InventoryAuditDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [audit, setAudit] = useState<InventoryAudit | null>(null);
  const [details, setDetails] = useState<AuditDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingDetail, setEditingDetail] = useState<AuditDetail | null>(null);
  const [viewDetail, setViewDetail] = useState<AuditDetail | null>(null);
  const [approvingDetail, setApprovingDetail] = useState<AuditDetail | null>(null);
  const [auditDocumentCode, setAuditDocumentCode] = useState('');
  const [approvingLoading, setApprovingLoading] = useState(false);

  // States cho duyệt thực hiện và hoàn thành kiểm kê
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [executionDocumentCode, setExecutionDocumentCode] = useState('');
  const [completionDocumentCode, setCompletionDocumentCode] = useState('');
  const [executionLoading, setExecutionLoading] = useState(false);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [form] = Form.useForm();
  const [locationOptions, setLocationOptions] = useState<{ value: number; label: string }[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<{ value: number; label: string }[]>([]);

  useEffect(() => {
    if (id) {
      loadAuditDetails();
      loadOptions();
    }
  }, [id]); // loadAuditDetails will be stable after first render

  const loadAuditDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await apiClient.getInventoryAuditById(Number(id)) as any;


      if (result && result.audit && result.details) {


        setAudit(result.audit);
        setDetails(result.details);
      } else {

        message.error('Dữ liệu không hợp lệ');
      }
    } catch (error) {

      message.error('Lỗi khi tải dữ liệu kiểm kê');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [employees] = await Promise.all([
        apiClient.getEmployees()
      ]);

      setEmployeeOptions((employees as any[]).map((emp: any) => ({ value: emp.id, label: emp.full_name })));
    } catch (error) {

    }
  };

  const loadLocationsByCompany = async (companyId: number) => {
    try {
      const locations = await apiClient.getLocationsByCompany(companyId);
      setLocationOptions((locations as any[]).map((loc: any) => ({ value: loc.id, label: loc.name })));
    } catch (error) {

    }
  };

  const handleEdit = (detail: AuditDetail) => {
    let snap: any = detail.snapshot;
    if (snap && typeof snap === 'string') {
      try { snap = JSON.parse(snap); } catch { snap = {}; }
    }
    if (!snap || typeof snap !== 'object') snap = {};

    // Load locations theo company của tài sản
    if (snap.company_id) {
      loadLocationsByCompany(snap.company_id);
    }

    form.setFieldsValue({
      result: detail.result === 'Chưa kiểm kê' ? undefined : detail.result,
      asset_status: snap.status || detail.asset_status,
      technical_condition: snap.technical_condition || detail.technical_condition,
      location_id: snap.location_id || detail.location_id,
      notes: detail.notes,
      proposal: detail.proposal,
    });
    setEditingDetail(detail);
  };

  const handleApproveAudit = async (auditId: number, auditDocumentCode: string) => {
    try {
      await apiClient.approveAuditDetail(auditId, auditDocumentCode);
      await loadAuditDetails(); // Reload data
    } catch (error) {
      throw error;
    }
  };

  const handleApproveFromList = async () => {
    if (!approvingDetail || !auditDocumentCode.trim()) {
      message.error('Vui lòng nhập mã hồ sơ kiểm kê');
      return;
    }

    setApprovingLoading(true);
    try {
      await apiClient.approveAuditDetail(approvingDetail.id, auditDocumentCode.trim());
      setApprovingDetail(null);
      setAuditDocumentCode('');
      await loadAuditDetails(); // Reload data
      message.success('Duyệt kiểm kê thành công');
    } catch (error) {
      message.error('Lỗi khi duyệt kiểm kê');
    } finally {
      setApprovingLoading(false);
    }
  };

  const handleApproveExecution = async () => {
    if (!executionDocumentCode.trim()) {
      message.error('Vui lòng nhập hồ sơ duyệt kiểm kê');
      return;
    }

    setExecutionLoading(true);
    try {

      await apiClient.approveExecution(Number(id), executionDocumentCode.trim());

      setShowExecutionModal(false);
      setExecutionDocumentCode('');
      await loadAuditDetails(); // Reload data
      message.success('Duyệt thực hiện kiểm kê thành công');
    } catch (error: any) {


      message.error(`Lỗi khi duyệt thực hiện kiểm kê: ${error.response?.data?.message || error.message}`);
    } finally {
      setExecutionLoading(false);
    }
  };

  const handleCompleteCampaign = async () => {
    if (!completionDocumentCode.trim()) {
      message.error('Vui lòng nhập hồ sơ hoàn thành kiểm kê');
      return;
    }

    setCompletionLoading(true);
    try {

      await apiClient.completeCampaignAudit(Number(id), completionDocumentCode.trim());

      setShowCompletionModal(false);
      setCompletionDocumentCode('');
      await loadAuditDetails(); // Reload data
      message.success('Hoàn thành kiểm kê định kỳ thành công');
    } catch (error: any) {


      message.error(`Lỗi khi hoàn thành kiểm kê định kỳ: ${error.response?.data?.message || error.message}`);
    } finally {
      setCompletionLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingDetail) return;

    try {
      const values = await form.validateFields();
      await apiClient.put(`/inventory-audit/${id}/audit-details/${editingDetail.asset_id}`, {
        ...values,
        audit_date: new Date().toISOString(),
      });

      message.success('Cập nhật kiểm kê thành công');
      setEditingDetail(null);
      form.resetFields();
      await loadAuditDetails();
    } catch (error) {

      message.error('Lỗi khi cập nhật kiểm kê');
    }
  };

  // Thêm hàm xử lý kiểm kê nhanh
  const handleQuickAudit = async (detail: AuditDetail) => {
    // Hiển thị xác nhận trước khi thực hiện
    Modal.confirm({
      title: 'Xác nhận kiểm kê nhanh',
      content: 'Bạn có chắc muốn đánh dấu tài sản này là "Tìm thấy"?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiClient.put(`/inventory-audit/${id}/audit-details/${detail.asset_id}`, {
            result: INVENTORY_AUDIT_RESULT.FOUND,
            audit_date: new Date().toISOString(),
          });

          message.success('Đã đánh dấu "Tìm thấy" cho tài sản này');
          await loadAuditDetails();
        } catch (error) {

          message.error('Lỗi khi thực hiện kiểm kê nhanh');
        }
      }
    });
  };

  // Export Excel function - Danh sách cơ bản (cho trình duyệt)
  const handleExportBasicExcel = () => {
    if (!audit || details.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }

    // Tạo dữ liệu cho Excel với thông tin cơ bản
    const excelData = [
      // Header bảng
      ['STT', 'Mã tài sản', 'Tên tài sản', 'Vị trí', 'Tình trạng tài sản', 'Tình trạng kỹ thuật'],
      // Dữ liệu tài sản
      ...details.map((detail, index) => {
        let snap: any = detail.snapshot;
        if (snap && typeof snap === 'string') {
          try { snap = JSON.parse(snap); } catch { snap = {}; }
        }

        // Chuyển đổi tình trạng tài sản
        const getAssetStatusText = (status: string) => {
          const statusMap: { [key: string]: string } = {
            'in_use': 'Đang sử dụng',
            'in_storage': 'Lưu kho',
            'under_maintenance': 'Đang bảo dưỡng',
            'disposed': 'Đã thanh lý',
            'lost': 'Mất',
            'damaged': 'Hỏng',
            'pending_deployment': 'Chờ triển khai'
          };
          return statusMap[status] || status;
        };

        // Chuyển đổi tình trạng kỹ thuật
        const getTechnicalConditionText = (condition: string) => {
          const conditionMap: { [key: string]: string } = {
            'new': 'Mới',
            'good': 'Tốt',
            'fair': 'Khá',
            'average': 'Trung bình',
            'poor': 'Kém',
            'bad': 'Hỏng'
          };
          return conditionMap[condition] || condition;
        };

        return [
          index + 1,
          snap?.asset_code || 'N/A',
          snap?.name || 'N/A',
          snap?.location_name || (snap?.location_id ? `Vị trí ${snap.location_id}` : 'N/A'),
          getAssetStatusText(detail.asset_status || snap?.status || ''),
          getTechnicalConditionText(detail.technical_condition || snap?.technical_condition || '')
        ];
      })
    ];

    // Tạo workbook và worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Định dạng cột
    const colWidths = [
      { wch: 8 },   // STT
      { wch: 20 },  // Mã tài sản
      { wch: 40 },  // Tên tài sản
      { wch: 25 },  // Vị trí
      { wch: 20 },  // Tình trạng tài sản
      { wch: 20 }   // Tình trạng kỹ thuật
    ];
    ws['!cols'] = colWidths;

    // Thêm worksheet vào workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Danh sách tài sản cơ bản');

    // Tên file
    const fileName = `Danh_sach_tai_san_co_ban_${audit.code}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;

    // Xuất file Excel
    XLSX.writeFile(wb, fileName);
    message.success('Đã xuất file Excel danh sách tài sản cơ bản');
  };

  // Export Excel function - Danh sách chi tiết (cho trình hoàn thành)
  const handleExportDetailedExcel = () => {
    if (!audit || details.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }

    // Tạo dữ liệu cho Excel với đầy đủ thông tin kiểm kê
    const excelData = [
      // Header bảng
      ['STT', 'Mã tài sản', 'Tên tài sản', 'Vị trí', 'Kết quả kiểm kê', 'Tình trạng tài sản', 'Tình trạng kỹ thuật', 'Đề xuất', 'Ghi chú'],
      // Dữ liệu tài sản
      ...details.map((detail, index) => {
        let snap: any = detail.snapshot;
        if (snap && typeof snap === 'string') {
          try { snap = JSON.parse(snap); } catch { snap = {}; }
        }

        // Chuyển đổi tình trạng tài sản
        const getAssetStatusText = (status: string) => {
          const statusMap: { [key: string]: string } = {
            'in_use': 'Đang sử dụng',
            'in_storage': 'Lưu kho',
            'under_maintenance': 'Đang bảo dưỡng',
            'disposed': 'Đã thanh lý',
            'lost': 'Mất',
            'damaged': 'Hỏng',
            'pending_deployment': 'Chờ triển khai'
          };
          return statusMap[status] || status;
        };

        // Chuyển đổi tình trạng kỹ thuật
        const getTechnicalConditionText = (condition: string) => {
          const conditionMap: { [key: string]: string } = {
            'new': 'Mới',
            'good': 'Tốt',
            'fair': 'Khá',
            'average': 'Trung bình',
            'poor': 'Kém',
            'bad': 'Hỏng'
          };
          return conditionMap[condition] || condition;
        };

        return [
          index + 1,
          snap?.asset_code || 'N/A',
          snap?.name || 'N/A',
          snap?.location_name || (snap?.location_id ? `Vị trí ${snap.location_id}` : 'N/A'),
          detail.result || 'Chưa kiểm kê',
          getAssetStatusText(detail.asset_status || snap?.status || ''),
          getTechnicalConditionText(detail.technical_condition || snap?.technical_condition || ''),
          detail.proposal || '',
          detail.notes || ''
        ];
      })
    ];

    // Tạo workbook và worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Định dạng cột
    const colWidths = [
      { wch: 8 },   // STT
      { wch: 20 },  // Mã tài sản
      { wch: 40 },  // Tên tài sản
      { wch: 25 },  // Vị trí
      { wch: 20 },  // Kết quả kiểm kê
      { wch: 20 },  // Tình trạng tài sản
      { wch: 20 },  // Tình trạng kỹ thuật
      { wch: 30 },  // Đề xuất
      { wch: 30 }   // Ghi chú
    ];
    ws['!cols'] = colWidths;

    // Thêm worksheet vào workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Kết quả kiểm kê chi tiết');

    // Tên file
    const fileName = `Ket_qua_kiem_ke_chi_tiet_${audit.code}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;

    // Xuất file Excel
    XLSX.writeFile(wb, fileName);
    message.success('Đã xuất file Excel kết quả kiểm kê chi tiết');
  };

  const columns: ColumnsType<AuditDetail> = [
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      width: 120,
      render: (_: any, record: AuditDetail) => {
        let snap: any = record.snapshot;
        if (snap && typeof snap === 'string') {
          try { snap = JSON.parse(snap); } catch { snap = {}; }
        }
        return snap?.asset_code || '—';
      },
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'asset_name',
      key: 'asset_name',
      width: 200,
      render: (_: any, record: AuditDetail) => {
        let snap: any = record.snapshot;
        if (snap && typeof snap === 'string') {
          try { snap = JSON.parse(snap); } catch { snap = {}; }
        }
        return snap?.name || '—';
      },
    },
    {
      title: 'Kết quả',
      dataIndex: 'result',
      key: 'result',
      width: 120,
      render: (result: string) => (
        <Tag color={result === 'Chưa kiểm kê' ? 'orange' : result === 'Tìm thấy' ? 'green' : 'red'}>
          {result}
        </Tag>
      ),
    },
    {
      title: 'Tình trạng',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'Chờ duyệt' ? 'orange' : status === 'Đã duyệt' ? 'green' : 'blue'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Đề xuất',
      dataIndex: 'proposal',
      key: 'proposal',
      width: 150,
      render: (proposal: string) => {
        if (!proposal) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Tooltip title={proposal}>
            <span style={{
              display: 'block',
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {proposal}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      render: (notes: string) => {
        if (!notes) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Tooltip title={notes}>
            <span style={{
              display: 'block',
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {notes}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 200,
      render: (_: any, record: AuditDetail) => (
        <Space>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setViewDetail(record)}
            />
          </Tooltip>

          {/* Nút kiểm kê nhanh - luôn hiện khi đợt kiểm kê chưa hoàn thành, nhưng disable khi đã kiểm kê */}
          {audit?.status !== 'Hoàn thành' && (
            <Tooltip title={record.result === 'Chưa kiểm kê' ? "Kiểm kê nhanh - Đánh dấu 'Tìm thấy' ngay lập tức" : "Đã kiểm kê"}>
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => handleQuickAudit(record)}
                disabled={record.result !== 'Chưa kiểm kê'}
                style={{
                  color: record.result === 'Chưa kiểm kê' ? '#52c41a' : '#d9d9d9',
                  cursor: record.result === 'Chưa kiểm kê' ? 'pointer' : 'not-allowed'
                }}
              />
            </Tooltip>
          )}

          {/* Nút chỉnh sửa chi tiết - hiện khi đã kiểm kê hoặc cần thay đổi thông tin */}
          {audit?.status !== 'Hoàn thành' && (
            <Tooltip title="Kiểm kê thay đổi">
              <Button
                type="text"
                icon={<FormOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}

          {/* Ẩn icon duyệt cho kiểm kê định kỳ và kiểm kê điều chuyển */}
          {audit?.type !== 'campaign' && audit?.type !== 'transfer' && (
            record.status === 'Chờ duyệt' ? (
              <Tooltip title="Duyệt kiểm kê">
                <Button
                  type="text"
                  icon={<ClockCircleOutlined />}
                  onClick={() => setApprovingDetail(record)}
                  style={{ color: '#faad14' }}
                />
              </Tooltip>
            ) : record.status === 'Đã duyệt' ? (
              <Tooltip title="Đã duyệt">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: '#52c41a' }}
                  disabled
                />
              </Tooltip>
            ) : null
          )}
        </Space>
      ),
    },
  ];




  if (!audit) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Đang tải...</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/inventory-audit')}
          style={{ padding: 4, height: 'auto' }}
        />
        <Title level={2} style={{ margin: 0 }}>
          {audit.name}
        </Title>
      </div>

      <Row gutter={24} style={{ alignItems: 'stretch', marginBottom: 24 }}>
        <Col span={16}>
          <Card title="Thông tin đợt kiểm kê" style={{ height: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>Mã kiểm kê:</div>
                  <div>{audit.code}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>Tên đợt kiểm kê:</div>
                  <div>{audit.name}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>Ngày bắt đầu:</div>
                  <div>{new Date(audit.start_date).toLocaleDateString('vi-VN')}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>
                    {audit.assigned_to_employee_id ? 'Nhân viên:' : audit.location_id ? 'Vị trí:' : 'Đối tượng:'}
                  </div>
                  <div>
                    {audit.assigned_employee_name || audit.location_name || '—'}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>Ngày kết thúc:</div>
                  <div>{new Date(audit.end_date).toLocaleDateString('vi-VN')}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>Trạng thái:</div>
                  <Tag color={audit.status === 'Hoàn thành' ? 'green' : audit.status === 'Đang tiến hành' ? 'blue' : 'orange'}>
                    {audit.status}
                  </Tag>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', color: '#666', marginBottom: 4 }}>Mô tả:</div>
                  <div>{audit.description || '—'}</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={8}>
          <Card title="Thống kê" style={{ height: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Statistic
                title="Tổng tài sản"
                value={details.length}
                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
              />
            </div>

            <Divider />

            <div style={{ fontSize: '14px', color: '#666' }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#52c41a' }}>✓ Đã kiểm kê:</span> {details.filter(d => d.result && d.result !== 'Chưa kiểm kê').length}
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#faad14' }}>⏳ Chưa kiểm kê:</span> {details.filter(d => !d.result || d.result === 'Chưa kiểm kê').length}
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#1890ff' }}>✓ Đã duyệt:</span> {details.filter(d => d.status === 'Đã duyệt').length}
              </div>
              <div>
                <span style={{ color: '#faad14' }}>⏳ Chờ duyệt:</span> {details.filter(d => d.status === 'Chờ duyệt').length}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Action buttons */}
      <Card title="Thao tác" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {audit.status === 'Chờ duyệt thực hiện' && (
            <div>
              <Button
                type="primary"
                size="large"
                onClick={() => setShowExecutionModal(true)}
                style={{ width: '100%' }}
              >
                Duyệt kiểm kê
              </Button>
              <div style={{
                marginTop: 8,
                padding: '8px',
                backgroundColor: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#1890ff',
                textAlign: 'center'
              }}>
                📋 Sau khi duyệt, bạn có thể bắt đầu kiểm kê tài sản
              </div>
            </div>
          )}

          {audit.status === 'Đang tiến hành' && details.length > 0 && details.every(detail => detail.result && detail.result !== 'Chưa kiểm kê') && (
            <div>
              <Button
                type="primary"
                size="large"
                onClick={() => setShowCompletionModal(true)}
                style={{ width: '100%' }}
              >
                Hoàn thành kiểm kê
              </Button>
              <div style={{
                marginTop: 8,
                padding: '8px',
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#52c41a',
                textAlign: 'center'
              }}>
                ✅ Tất cả tài sản đã được kiểm kê - có thể hoàn thành đợt kiểm kê
              </div>
            </div>
          )}

          {audit.status === 'Đang tiến hành' && details.length > 0 && !details.every(detail => detail.result && detail.result !== 'Chưa kiểm kê') && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '6px',
              color: '#d46b08',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              ⚠️ Cần kiểm kê tất cả tài sản trước khi hoàn thành đợt kiểm kê<br />
              <span style={{ fontSize: '12px', color: '#999' }}>
                💡 Sử dụng nút "Kiểm kê nhanh" để tăng tốc quá trình kiểm kê
              </span>
            </div>
          )}

          {/* Export Excel button - Danh sách cơ bản (cho trình duyệt) */}
          {audit.status === 'Chờ duyệt thực hiện' && details.length > 0 && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Xuất danh sách tài sản cơ bản:</div>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={() => handleExportBasicExcel()}
                size="large"
              >
                Xuất Excel
              </Button>
              <div style={{
                marginTop: 8,
                fontSize: '12px',
                color: '#666',
                textAlign: 'center'
              }}>
                📋 File Excel để trình duyệt trên hệ thống văn phòng điện tử
              </div>
            </div>
          )}

          {/* Export Excel button - Danh sách chi tiết (cho trình hoàn thành) */}
          {(audit.status === 'Hoàn thành' || (audit.status === 'Đang tiến hành' && details.length > 0 && details.every(detail => detail.result && detail.result !== 'Chưa kiểm kê'))) && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Xuất danh sách tài sản chi tiết:</div>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={() => handleExportDetailedExcel()}
                size="large"
              >
                Xuất Excel
              </Button>
              <div style={{
                marginTop: 8,
                fontSize: '12px',
                color: '#666',
                textAlign: 'center'
              }}>
                📊 File Excel chứa thông tin đã kiểm kê, thay đổi, ghi chú, đề xuất
              </div>
            </div>
          )}
        </Space>
      </Card>

      <Card
        title={`Danh sách tài sản kiểm kê (${details.length})`}
      >
        {audit.status === 'Đang tiến hành' && (
          <div style={{
            marginBottom: 16,
            padding: '12px',
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <div style={{ color: '#1890ff', fontWeight: 'bold', marginBottom: 4 }}>💡 Mẹo kiểm kê nhanh:</div>
            <div style={{ color: '#666' }}>
              • Sử dụng nút <span style={{ color: '#52c41a', fontWeight: 'bold' }}>✓</span> để đánh dấu "Tìm thấy" ngay lập tức<br />
              • Sử dụng nút <span style={{ color: '#1890ff', fontWeight: 'bold' }}>📝</span> để kiểm kê thay đổi thông tin và ghi chú
            </div>
          </div>
        )}
        <Table
          dataSource={details}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
        />
      </Card>

      {/* Modal chỉnh sửa */}
      <Modal
        title="Kiểm kê thay đổi"
        open={!!editingDetail}
        onOk={handleSave}
        onCancel={() => {
          setEditingDetail(null);
          form.resetFields();
        }}
        width={600}
      >
        <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
          <div style={{ color: '#52c41a', fontWeight: 'bold', marginBottom: 4 }}>💡 Ghi chú:</div>
          <div style={{ color: '#666', fontSize: '14px' }}>
            Sử dụng form này để thay đổi kết quả kiểm kê, cập nhật thông tin tài sản hoặc thêm ghi chú bổ sung khi cần thiết.
          </div>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item
            name="result"
            label="Kết quả kiểm kê"
            rules={[
              { required: true, message: 'Vui lòng chọn kết quả kiểm kê' },
              {
                validator: (_, value) => {
                  if (value && (value === INVENTORY_AUDIT_RESULT.FOUND || value === INVENTORY_AUDIT_RESULT.MISSING)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Vui lòng chọn kết quả kiểm kê'));
                }
              }
            ]}
          >
            <Select placeholder="Chọn kết quả kiểm kê">
              <Select.Option value={INVENTORY_AUDIT_RESULT.FOUND}>Tìm thấy</Select.Option>
              <Select.Option value={INVENTORY_AUDIT_RESULT.MISSING}>Không tìm thấy</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="asset_status" label="Tình trạng tài sản">
            <Select>
              <Select.Option value="in_use">Đang sử dụng</Select.Option>
              <Select.Option value="in_storage">Lưu kho</Select.Option>
              <Select.Option value="under_maintenance">Đang bảo dưỡng</Select.Option>
              <Select.Option value="disposed">Đã thanh lý</Select.Option>
              <Select.Option value="lost">Mất</Select.Option>
              <Select.Option value="damaged">Hỏng</Select.Option>
              <Select.Option value="pending_deployment">Chờ triển khai</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="technical_condition" label="Tình trạng kỹ thuật">
            <Select>
              <Select.Option value="new">Mới</Select.Option>
              <Select.Option value="good">Tốt</Select.Option>
              <Select.Option value="fair">Khá</Select.Option>
              <Select.Option value="average">Trung bình</Select.Option>
              <Select.Option value="poor">Kém</Select.Option>
              <Select.Option value="bad">Hỏng</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="location_id" label="Vị trí">
            <Select
              showSearch
              placeholder="Chọn vị trí"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={locationOptions}
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="proposal" label="Đề xuất">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal xem chi tiết */}
      <AuditDetailModal
        visible={!!viewDetail}
        onClose={() => setViewDetail(null)}
        auditDetail={viewDetail ? {
          ...viewDetail,
          audit_date: viewDetail.audit_date || new Date().toISOString(),
          result: viewDetail.result || '',
          status: viewDetail.status || '',
          type: viewDetail.type || '',
          technical_condition: viewDetail.technical_condition || '',
          location_id: viewDetail.location_id || 0,
          accessories_snapshot: viewDetail.accessories_snapshot ?
            (typeof viewDetail.accessories_snapshot === 'string' ?
              JSON.parse(viewDetail.accessories_snapshot) : viewDetail.accessories_snapshot) :
            undefined
        } : null}
        auditHistory={details.map(detail => ({
          ...detail,
          audit_date: detail.audit_date || new Date().toISOString(),
          result: detail.result || '',
          status: detail.status || '',
          type: detail.type || '',
          technical_condition: detail.technical_condition || '',
          location_id: detail.location_id || 0,
          accessories_snapshot: detail.accessories_snapshot ?
            (typeof detail.accessories_snapshot === 'string' ?
              JSON.parse(detail.accessories_snapshot) : detail.accessories_snapshot) :
            undefined
        }))}
        locations={locationOptions.map(loc => ({ id: loc.value, name: loc.label }))}
        companies={[]}
        categories={[]}
        suppliers={[]}
        employees={employeeOptions.map(emp => ({ id: emp.value, full_name: emp.label }))}
        onSaveNotes={async () => { }}
        onApproveAudit={handleApproveAudit}
        showApproveButton={true}
        title="Chi tiết kiểm kê"
      />

      {/* Modal duyệt kiểm kê */}
      <Modal
        title="Duyệt kiểm kê"
        open={!!approvingDetail}
        onCancel={() => {
          setApprovingDetail(null);
          setAuditDocumentCode('');
        }}
        onOk={handleApproveFromList}
        confirmLoading={approvingLoading}
        okText="Duyệt"
        cancelText="Hủy"
        okButtonProps={{
          disabled: !auditDocumentCode.trim()
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Vui lòng nhập mã hồ sơ kiểm kê để duyệt.
          </p>
          <Input
            placeholder="Nhập mã hồ sơ kiểm kê"
            value={auditDocumentCode}
            onChange={(e) => setAuditDocumentCode(e.target.value)}
          />
        </div>
      </Modal>

      {/* Modal duyệt thực hiện kiểm kê */}
      <Modal
        title="Duyệt thực hiện kiểm kê"
        open={showExecutionModal}
        onCancel={() => {
          setShowExecutionModal(false);
          setExecutionDocumentCode('');
        }}
        onOk={handleApproveExecution}
        confirmLoading={executionLoading}
        okText="Duyệt"
        cancelText="Hủy"
        okButtonProps={{
          disabled: !executionDocumentCode.trim()
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Vui lòng nhập hồ sơ duyệt kiểm kê tài sản.
          </p>
          <Input
            placeholder="Nhập hồ sơ duyệt kiểm kê"
            value={executionDocumentCode}
            onChange={(e) => setExecutionDocumentCode(e.target.value)}
          />
        </div>
      </Modal>

      {/* Modal hoàn thành kiểm kê định kỳ */}
      <Modal
        title="Hoàn thành kiểm kê định kỳ"
        open={showCompletionModal}
        onCancel={() => {
          setShowCompletionModal(false);
          setCompletionDocumentCode('');
        }}
        onOk={handleCompleteCampaign}
        confirmLoading={completionLoading}
        okText="Hoàn thành"
        cancelText="Hủy"
        okButtonProps={{
          disabled: !completionDocumentCode.trim()
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Vui lòng nhập hồ sơ hoàn thành kiểm kê định kỳ. Hồ sơ này cũng sẽ được dùng làm hồ sơ kiểm kê cho từng tài sản.
          </p>
          <Input
            placeholder="Nhập hồ sơ hoàn thành kiểm kê"
            value={completionDocumentCode}
            onChange={(e) => setCompletionDocumentCode(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

// Wrap with Error Boundary for better error handling (clean code improvement)
const InventoryAuditDetailPageWithErrorBoundary: React.FC = () => (
  <AuditErrorBoundary>
    <InventoryAuditDetailPage />
  </AuditErrorBoundary>
);

export default InventoryAuditDetailPageWithErrorBoundary; 
